import React, { useState, useEffect, useCallback } from 'react';
import { Spin, notification, Input } from 'antd';
import { 
    Warning, 
    ClipboardText, 
    Notepad, 
    Info,
    FloppyDisk,
    X
} from '@phosphor-icons/react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import designServices from '../../../../services/oneButtonServices/designServices';
import DashboardModal from './DashboardModal';
import './oneButton-css/RoofProposalDetailModal.css';

interface RoofProposalDetailModalProps {
    open: boolean;
    onClose: () => void;
    /** Stack above parent dashboards (e.g. Roof Estimates). */
    zIndex?: number;
}

// Initial form state with default values for all backend fields
const INITIAL_FORM_STATE = {
    organicGrowth: false,
    degranulation: false,
    staining: false,
    curling: false,
    cracking: false,
    missingShingles: false,
    skylights: false,
    concerns: '',
    notes: '',
    Total_OneYear_Price: 0,
    Term: '',
    APR: 0,
    Monthly_Payment: 0,
    Referral_Rate: 0,
    Referral_Term: '',
    Referral_APR: 0,
    Referral_Monthly_Payment: 0,
};

// Configuration for warning items including their labels
const WARNING_ITEMS = [
    { label: 'Organic Growth', key: 'organicGrowth' },
    { label: 'Degranulation', key: 'degranulation' },
    { label: 'Staining', key: 'staining' },
    { label: 'Skylights', key: 'skylights' },
    { label: 'Curling', key: 'curling' },
    { label: 'Cracking', key: 'cracking' },
    { label: 'Missing Shingles', key: 'missingShingles' },
];

export const RoofProposalDetailModal: React.FC<RoofProposalDetailModalProps> = ({
    open,
    onClose,
    zIndex = 12100,
}) => {
    const { theme, opportunityID } = useOneButtonStore();
    const [noticeAPI, contextHolder] = notification.useNotification();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState(INITIAL_FORM_STATE);

    // Standardized notification helper for theme-aware feedback
    const notify = useCallback((type: 'success' | 'error' | 'warning' | 'info', message: string, description?: string) => {
        noticeAPI[type]({
            message,
            description,
            placement: 'topRight',
            className: `obd-notification obd-theme-${theme}`,
            style: { zIndex: 10001 }
        });
    }, [noticeAPI, theme]);

    // Fetches existing roof details and synchronizes them with the form state
    const retrieveRoofDetails = useCallback(async () => {
        if (!opportunityID) return;
        setLoading(true);
        try {
            const roofData = await designServices.RetrieveRoofProposalDetail(opportunityID);
            if (Array.isArray(roofData) && roofData.length > 0) {
                const data = roofData[0];
                setFormData({
                    organicGrowth: !!data.Organic_Growth,
                    degranulation: !!data.Degranulation,
                    staining: !!data.Staining,
                    curling: !!data.Curling,
                    cracking: !!data.Cracking,
                    missingShingles: !!data.Missing_Shingles,
                    skylights: !!data.Skylights,
                    concerns: data.Concerns || '',
                    notes: data.Notes || '',
                    Total_OneYear_Price: data.Total_OneYear_Price || 0,
                    Term: data.Term || '',
                    APR: data.APR || 0,
                    Monthly_Payment: data.Monthly_Payment || 0,
                    Referral_Rate: data.Referral_Rate || 0,
                    Referral_Term: data.Referral_Term || '',
                    Referral_APR: data.Referral_APR || 0,
                    Referral_Monthly_Payment: data.Referral_Monthly_Payment || 0,
                });
            }
        } catch (err) {
            console.error('Failed to retrieve roof details:', err);
        } finally {
            setLoading(false);
        }
    }, [opportunityID]);

    // Triggers data retrieval whenever the modal is opened
    useEffect(() => {
        if (open) {
            setFormData(INITIAL_FORM_STATE);
            retrieveRoofDetails();
        }
    }, [open, retrieveRoofDetails]);

    // Persists the current form data to the backend with a standardized payload
    const handleSave = async () => {
        if (!opportunityID) return;
        setSaving(true);
        try {
            const payload = {
                roofProposalDetailId: 0,
                opportunity_Id: opportunityID,
                organic_Growth: formData.organicGrowth,
                degranulation: formData.degranulation,
                staining: formData.staining,
                curling: formData.curling,
                cracking: formData.cracking,
                missing_Shingles: formData.missingShingles,
                skylights: formData.skylights,
                concerns: formData.concerns,
                notes: formData.notes,
                total_OneYear_Price: formData.Total_OneYear_Price,
                term: formData.Term,
                apr: formData.APR,
                monthly_Payment: formData.Monthly_Payment,
                referral_Rate: formData.Referral_Rate,
                referral_Term: formData.Referral_Term,
                referral_APR: formData.Referral_APR,
                referral_Monthly_Payment: formData.Referral_Monthly_Payment,
            };

            const response = await designServices.SaveRoofProposalDetail(payload);
            notify('success', 'Success', response?.Message || response?.message || 'Design Proposal saved successfully');
            onClose();
        } catch (err: any) {
            console.error('Failed to save roof details:', err);
            const errMsg = err?.response?.data?.Message || err?.message || 'Failed to save design proposal';
            notify('error', 'Error', errMsg);
        } finally {
            setSaving(false);
        }
    };

    // Generic field update helper to maintain immutable state updates
    const updateField = (field: string, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    return (
        <DashboardModal
            open={open}
            onClose={() => !(loading || saving) && onClose()}
            title="Roof Proposal Details"
            footer={
                <div className="obd-roof-modal__actions">
                    <button type="button" className="obd-roof-modal__btn obd-roof-modal__btn--cancel" onClick={onClose} disabled={saving || loading}>
                        <X size={14} weight="bold" />
                        Cancel
                    </button>
                    <button type="button" className="obd-roof-modal__btn obd-roof-modal__btn--primary" onClick={handleSave} disabled={saving || loading}>
                        <FloppyDisk size={14} weight="bold" />
                        Save
                    </button>
                </div>
            }
            centered
            width={1000}
            theme={theme}
            className={`obd-roof-proposal-modal obd-theme-${theme}`}
            rootClassName={`obd-roof-proposal-modal-root obd-theme-${theme}`}
            zIndex={zIndex}
            destroyOnClose
        >
            {contextHolder}
            <div className="obd-roof-modal" style={{ position: 'relative' }}>
                <div className="obd-rpm-modal__body">
                    <div className="obd-rpm-modal__warning-section">
                        <div className="obd-rpm-modal__section-label-container">
                            <Warning size={14} weight="bold" color="#f87171" style={{ marginRight: '8px' }} /> 
                            <span>WARNING SIGNS</span>
                        </div>
                        <div className="obd-rpm-modal__warning-grid">
                            {WARNING_ITEMS.map(item => (
                                <div 
                                    key={item.key} 
                                    className={`obd-rpm-modal__warning-item ${(formData as any)[item.key] ? 'obd-rpm-modal__warning-item--active' : ''}`}
                                    onClick={() => updateField(item.key, !(formData as any)[item.key])}
                                >
                                    <span className="obd-rpm-modal__warning-label">{item.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="obd-rpm-modal__important-badge" style={{ marginBottom: '16px' }}>
                        <Info size={12} weight="bold" />
                        <span><strong>IMPORTANT</strong> These notes will be visible on the Roof Proposal document</span>
                    </div>

                    <div className="obd-rpm-modal__notes-group">
                        <div className="obd-rpm-modal__section">
                            <div className="obd-rpm-modal__section-label">
                                <Notepad size={14} weight="bold" /> NOTES
                            </div>
                            <Input.TextArea 
                                className="obd-rpm-modal__textarea"
                                rows={6}
                                maxLength={120}
                                value={formData.notes}
                                onChange={(e) => updateField('notes', e.target.value)}
                            />
                            <div className="obd-rpm-modal__char-count">{formData.notes.length}/120</div>
                        </div>

                        <div className="obd-rpm-modal__section">
                            <div className="obd-rpm-modal__section-label">
                                <ClipboardText size={14} weight="bold" /> Custom Project Details
                            </div>
                            <Input.TextArea 
                                className="obd-rpm-modal__textarea"
                                rows={6}
                                maxLength={300}
                                value={formData.concerns}
                                onChange={(e) => updateField('concerns', e.target.value)}
                            />
                            <div className="obd-rpm-modal__char-count">{formData.concerns.length}/300</div>
                        </div>
                    </div>
                </div>

                {(loading || saving) && (
                    <div style={{ 
                        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, 
                        background: theme === 'dark' ? 'rgba(15, 23, 42, 0.4)' : 'rgba(255, 255, 255, 0.4)',
                        backdropFilter: 'blur(1px)', zIndex: 1000, borderRadius: 16
                    }}>
                        <Spin />
                        <span style={{ 
                            fontWeight: 500, 
                            color: theme === 'dark' ? 'rgba(248, 250, 252, 0.98)' : '#1e293b' 
                        }}>
                            {loading ? 'Loading details…' : 'Saving details…'}
                        </span>
                    </div>
                )}
            </div>
        </DashboardModal>
    );
};
